import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { maskCEP, maskPhone, maskCNPJ } from '../utils/formatters';
import SlotInput from './SlotInput';

interface CompanySubscriptionCreateModalProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
  onSave: () => void;
}

const CompanySubscriptionCreateModal: React.FC<CompanySubscriptionCreateModalProps> = ({
  companyId,
  companyName,
  onClose,
  onSave
}) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    company_plan_id: '',
    candidates_database_role: 2,
    accept_terms_required: false,
    closing_day: 1,
    interval_months: 1,
    payment_method: 'Iugu',
    mrr_value: 0,
    is_pre_paid: false,
    candidates_database_alerts_per_month: 0,
    candidates_database_contacts: 0,
    display_contacts_listing_days: 30,
    caesar_slots: -1,
    opportunities_slots: -1,
    recruiters_slots: -1,
    highlight_slots: -1,
    confidentials: -1,
    active: 1,
    billing_cnpj: '',
    billing_name: '',
    billing_email: '',
    billing_phone: '',
    billing_info: '',
    billing_zip: '',
    billing_neighborhood: '',
    billing_address1: '',
    billing_number: '',
    billing_address2: '',
    billing_city: '',
    billing_state: '',
    billing_simples_nacional: false
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setInitialLoading(true);
    await Promise.all([fetchPlans(), fetchCompanyBilling()]);
    setInitialLoading(false);
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/company-plans');
      setPlans(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar planos');
    }
  };

  const fetchCompanyBilling = async () => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      const company = response.data;
      
      if (company) {
        const addresses = company.addresses || [];
        const address = addresses[0] || {};
        const billing = company.billing || {};
        
        setFormData(prev => ({
          ...prev,
          billing_cnpj: maskCNPJ(billing.cnpj || company.cnpj || ''),
          billing_name: billing.name || company.name || '',
          billing_email: billing.email || company.email || '',
          billing_phone: maskPhone(billing.phone || address.telephone || ''),
          billing_info: billing.info || '',
          billing_zip: maskCEP(billing.zip || address.zip_code || ''),
          billing_neighborhood: billing.neighborhood || address.neighborhood || '',
          billing_address1: billing.address1 || address.street || '',
          billing_number: billing.number || address.number || '',
          billing_address2: billing.address2 || address.complement || '',
          billing_city: billing.city || address.city || '',
          billing_state: billing.state || address.state || '',
          billing_simples_nacional: billing.simples_nacional === 1
        }));
      }
    } catch (error) {
      console.error('Error loading company billing:', error);
    }
  };

  const handlePlanChange = async (planId: string) => {
    handleChange('company_plan_id', planId);
    
    if (!planId) return;
    
    try {
      const response = await api.get(`/company-plans/${planId}`);
      const plan = response.data;
      
      let features: any = {};
      if (plan.features) {
        try {
          features = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features;
        } catch (e) {
          features = {};
        }
      }
      
      setFormData(prev => ({
        ...prev,
        company_plan_id: planId,
        mrr_value: plan.price_cents ? plan.price_cents / 100 : prev.mrr_value,
        candidates_database_role: features.candidates_database_role ?? prev.candidates_database_role,
        candidates_database_alerts_per_month: features.candidates_database_alerts_per_month ?? prev.candidates_database_alerts_per_month,
        candidates_database_contacts: features.candidates_database_contacts ?? prev.candidates_database_contacts,
        display_contacts_listing_days: features.display_contacts_listing_days ?? prev.display_contacts_listing_days,
        caesar_slots: features.caesar_slots ?? prev.caesar_slots,
        opportunities_slots: features.opportunities_slots ?? prev.opportunities_slots,
        recruiters_slots: features.recruiters_slots ?? prev.recruiters_slots,
        highlight_slots: features.highlight_slots ?? prev.highlight_slots,
        confidentials: features.confidentials ?? prev.confidentials
      }));
    } catch (error: any) {
      console.error('Error loading plan details:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return; // Previne múltiplas submissões
    
    setLoading(true);
    try {
      await api.post(`/companies/${companyId}/subscriptions`, formData);
      toast.success('Assinatura criada com sucesso!');
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar assinatura');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, billing_phone: maskPhone(value) }));
  };

  const handleCEPChange = (value: string) => {
    setFormData(prev => ({ ...prev, billing_zip: maskCEP(value) }));
  };

  const handleCNPJChange = (value: string) => {
    setFormData(prev => ({ ...prev, billing_cnpj: maskCNPJ(value) }));
  };

  const handleNumberChange = (value: string) => {
    const numbersOnly = value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, billing_number: numbersOnly }));
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Nova Assinatura - {companyName}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.content}>
          {initialLoading ? (
            <div style={styles.loadingContainer}>Carregando...</div>
          ) : (
          <div style={styles.columns}>
            <div style={styles.leftColumn}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Dados do Plano</h3>
                
                <label style={styles.label}>
                  Banco de Talentos
                  <select
                    value={formData.candidates_database_role}
                    onChange={(e) => handleChange('candidates_database_role', Number(e.target.value))}
                    style={styles.select}
                  >
                    <option value={2}>ACESSO COMPLETO</option>
                    <option value={1}>ACESSO BÁSICO</option>
                    <option value={0}>SEM ACESSO</option>
                  </select>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.accept_terms_required}
                    onChange={(e) => handleChange('accept_terms_required', e.target.checked)}
                  />
                  PRECISA ACEITAR OS TERMOS DE USO
                </label>

                <label style={styles.label}>
                  Plano
                  <select
                    value={formData.company_plan_id}
                    onChange={(e) => handlePlanChange(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Selecione um plano</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.id}>{plan.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Dados de cobrança</h3>
                
                <label style={styles.label}>
                  CNPJ
                  <input 
                    type="text" 
                    value={formData.billing_cnpj} 
                    onChange={(e) => handleCNPJChange(e.target.value)} 
                    style={styles.input} 
                    maxLength={18}
                    placeholder="12.345.678/0001-90"
                  />
                </label>
                <label style={styles.label}>
                  Nome
                  <input type="text" value={formData.billing_name} onChange={(e) => handleChange('billing_name', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  Email
                  <input type="email" value={formData.billing_email} onChange={(e) => handleChange('billing_email', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  Telefone
                  <input 
                    type="text" 
                    value={formData.billing_phone} 
                    onChange={(e) => handlePhoneChange(e.target.value)} 
                    style={styles.input} 
                    maxLength={15}
                    placeholder="(11) 99999-9999"
                  />
                </label>
                <label style={styles.label}>
                  Info
                  <input type="text" value={formData.billing_info} onChange={(e) => handleChange('billing_info', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  CEP
                  <input 
                    type="text" 
                    value={formData.billing_zip} 
                    onChange={(e) => handleCEPChange(e.target.value)} 
                    style={styles.input} 
                    maxLength={9}
                    placeholder="12345-678"
                  />
                </label>
                <label style={styles.label}>
                  Bairro
                  <input type="text" value={formData.billing_neighborhood} onChange={(e) => handleChange('billing_neighborhood', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  Endereço
                  <input type="text" value={formData.billing_address1} onChange={(e) => handleChange('billing_address1', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  Número
                  <input 
                    type="text" 
                    value={formData.billing_number} 
                    onChange={(e) => handleNumberChange(e.target.value)} 
                    style={styles.input}
                    placeholder="123"
                  />
                </label>
                <label style={styles.label}>
                  Complemento
                  <input type="text" value={formData.billing_address2} onChange={(e) => handleChange('billing_address2', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  Cidade
                  <input type="text" value={formData.billing_city} onChange={(e) => handleChange('billing_city', e.target.value)} style={styles.input} />
                </label>
                <label style={styles.label}>
                  Estado
                  <input type="text" value={formData.billing_state} onChange={(e) => handleChange('billing_state', e.target.value)} style={styles.input} maxLength={2} />
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={formData.billing_simples_nacional} onChange={(e) => handleChange('billing_simples_nacional', e.target.checked)} />
                  Optante pelo simples nacional
                </label>
              </div>
            </div>

            <div style={styles.rightColumn}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Dados do pagamento</h3>
                
                <label style={styles.label}>
                  Dia do vencimento
                  <select
                    value={formData.closing_day}
                    onChange={(e) => handleChange('closing_day', Number(e.target.value))}
                    style={styles.select}
                  >
                    {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </label>

                <label style={styles.label}>
                  Intervalo (meses)
                  <select
                    value={formData.interval_months}
                    onChange={(e) => handleChange('interval_months', Number(e.target.value))}
                    style={styles.select}
                  >
                    {Array.from({length: 24}, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </label>

                <label style={styles.label}>
                  Método de pagamento
                  <select
                    value={formData.payment_method}
                    onChange={(e) => handleChange('payment_method', e.target.value)}
                    style={styles.select}
                  >
                    <option value="Iugu">Iugu</option>
                    <option value="boleto">boleto</option>
                    <option value="credito">credito</option>
                    <option value="Outro">Outro</option>
                  </select>
                </label>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Valores</h3>
                
                <label style={styles.label}>
                  Valor bruto (MRR)
                  <input
                    type="number"
                    step="0.01"
                    value={formData.mrr_value}
                    onChange={(e) => handleChange('mrr_value', Number(e.target.value))}
                    style={styles.input}
                  />
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.is_pre_paid}
                    onChange={(e) => handleChange('is_pre_paid', e.target.checked)}
                  />
                  PRÉ-PAGO
                </label>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Banco de Talentos</h3>
                
                <label style={styles.label}>
                  Total de alertas
                  <input
                    type="number"
                    value={formData.candidates_database_alerts_per_month}
                    onChange={(e) => handleChange('candidates_database_alerts_per_month', Number(e.target.value))}
                    style={styles.input}
                  />
                </label>

                <label style={styles.label}>
                  Qtd de contatos abertos
                  <input
                    type="number"
                    value={formData.candidates_database_contacts}
                    onChange={(e) => handleChange('candidates_database_contacts', Number(e.target.value))}
                    style={styles.input}
                  />
                </label>

                <label style={styles.label}>
                  Tempo de listagem dos contatos abertos (dias)
                  <input
                    type="number"
                    value={formData.display_contacts_listing_days}
                    onChange={(e) => handleChange('display_contacts_listing_days', Number(e.target.value))}
                    style={styles.input}
                  />
                </label>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Caesar</h3>
                
                <SlotInput
                  label="SLOTS"
                  value={formData.caesar_slots}
                  onChange={(value) => handleChange('caesar_slots', value)}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Vagas</h3>
                
                <SlotInput
                  label="Slots de Vagas"
                  value={formData.opportunities_slots}
                  onChange={(value) => handleChange('opportunities_slots', value)}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />

                <SlotInput
                  label="Slots de Recrutadores"
                  value={formData.recruiters_slots}
                  onChange={(value) => handleChange('recruiters_slots', value)}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />

                <SlotInput
                  label="Slots de destaques"
                  value={formData.highlight_slots}
                  onChange={(value) => handleChange('highlight_slots', value)}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />

                <SlotInput
                  label="Qtd. Confidenciais"
                  value={formData.confidentials}
                  onChange={(value) => handleChange('confidentials', value)}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
              </div>
            </div>
          </div>
          )}

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} style={styles.saveBtn}>
              {loading ? 'Salvando...' : 'Criar Assinatura'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '20px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '20px',
    maxHeight: 'calc(90vh - 140px)',
    overflow: 'auto',
  },
  columns: {
    display: 'flex',
    gap: '20px',
  },
  leftColumn: {
    flex: '1',
    minWidth: '400px',
  },
  rightColumn: {
    flex: '1',
    minWidth: '400px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '12px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: '12px',
    fontSize: '14px',
    color: colors.text,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '14px',
    color: colors.text,
  },
  input: {
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '4px',
  },
  select: {
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '20px',
    borderTop: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    padding: '10px 20px',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '10px 20px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  loadingContainer: {
    textAlign: 'center' as const,
    padding: '40px',
    color: colors.textLight,
    fontSize: '16px',
  },
};

export default CompanySubscriptionCreateModal;
